import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Especialidad } from 'src/app/models/Especialidad';
import { Prestacion } from 'src/app/models/Prestacion';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-prestacion',
    templateUrl: './prestacion.component.html',
    styleUrls: ['./prestacion.component.css'],
    standalone: false
})
export class PrestacionComponent {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private aRoute = inject(ActivatedRoute);

  // estado
  error   = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  id?: number;
  operacion = 'Agregar ';
  botonOperacion = 'Agregar ';

  especialidades: Especialidad[] = [];
  especialidadTexto = signal<string>('');

  // forms
  morePrestacion!: FormGroup;

  constructor(){
      this.createForm();
      this.id = Number(this.aRoute.snapshot.paramMap.get('id'));
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();

        const collator = new Intl.Collator('es', { sensitivity: 'base', ignorePunctuation: true });
        const especialidades = await firstValueFrom(this._usuarioService.getEspecialidades());
        this.especialidades = (especialidades ?? [])
          .sort((a, b) => collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? ''));

        // Si hay id => edición
        if(this.id != 0){
          // Es editar
          this.operacion = "Editar ";
          this.botonOperacion = "Actualizar ";

          const res = await firstValueFrom(this._usuarioService.getPrestacion(this.id!));

          if (!res) {
            // No existe => no tiene sentido editar
            this.error.set('La prestación no existe o fue eliminada.');
            this.router.navigate(['/more-prestaciones']);
            return;
          }

          this.morePrestacion.patchValue({
            especialidad: res?.id_especialidad ?? '',
            descripcion: res?.x_descripcion ?? ''
          });

          const especialidadActual = this.especialidades.find(e => e.id === res?.id_especialidad);
          this.especialidadTexto.set(especialidadActual?.x_descripcion ?? '');
        }
      } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar tu cuenta.';
        this.router.navigate(['/sign-in']);
      }
    });
  }

  // El combo de especialidad usa un <input> + <datalist>: el navegador va filtrando las
  // opciones a medida que se escribe. Acá solo resolvemos qué id_especialidad corresponde
  // al texto tipeado (o lo limpiamos si no matchea ninguna especialidad exacta).
  onEspecialidadInput(texto: string): void {
    this.especialidadTexto.set(texto);

    const match = this.especialidades.find(e => normalizeText(e.x_descripcion) === normalizeText(texto));
    const control = this.morePrestacion.controls['especialidad'];
    control.setValue(match?.id ?? null);
    control.markAsDirty();
  }

  onEspecialidadBlur(): void {
    const control = this.morePrestacion.controls['especialidad'];
    if (control.value == null) {
      control.markAsTouched();
      return;
    }

    // si lo que quedó tipeado no coincide con la especialidad seleccionada, se descarta
    const actual = this.especialidades.find(e => e.id === control.value);
    if (!actual || normalizeText(actual.x_descripcion) !== normalizeText(this.especialidadTexto())) {
      this.especialidadTexto.set(actual?.x_descripcion ?? '');
    }
    control.markAsTouched();
  }

  createForm() {
    this.morePrestacion = this._fb.group({
      especialidad: [null, Validators.required ],
      descripcion: ['', Validators.required ]
    });
  }

  async savePrestacion(){
    // Evita doble submit si ya está cargando
    if (this.loading()) return;
    
    this.error.set('');

    const v = this.morePrestacion.getRawValue();

    if (!v.especialidad) { this.error.set('Debe seleccionar la Especialidad'); return; }
    if (!v.descripcion?.trim()) { this.error.set('Debe completar la Descripción'); return; }

    // Respeta validaciones de template/reactivas
    if (this.morePrestacion.invalid) return;

    const isEdit = !!this.id;
        
    const prestacion: Prestacion = {
      id_especialidad: v.especialidad,
      x_descripcion: v.descripcion.trim()
    }

    try {
      await this._loadingService.run(async () => {
        if (this.id !== 0) {
          // Editar
          await firstValueFrom(this._usuarioService.updatePrestacion(prestacion));
        } else {
          // Crear
          await firstValueFrom(this._usuarioService.createPrestacion(prestacion));
        }
      });

      await this.router.navigate(['/more-prestaciones']);
      } catch (e) {
      console.error(e);
      this.error.set(this.id !== 0
        ? 'Error al actualizar la Prestación'
        : 'Error al agregar la Prestación');
    }
  }

  back(){
    this._location.back();
  }
}
